import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import OfferCard from "@/components/OfferCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, Tag, Store } from "lucide-react";

const CATEGORY_INTRO = {
  restaurants: {
    h1: "Les meilleurs restaurants de {city}",
    intro: "Découvrez les restaurants partenaires PROXIreduc-28 à {city} : brasseries, cuisine traditionnelle, pizzerias, cuisine du monde. Chaque établissement propose un avantage négocié exclusivement pour les habitants d'Eure-et-Loir."
  },
  commerces: {
    h1: "Les commerces de {city} à prix avantageux",
    intro: "Faites vos courses à {city} tout en bénéficiant de réductions chez les commerçants locaux partenaires de PROXIreduc-28."
  },
  loisirs: {
    h1: "Loisirs et sorties à {city}",
    intro: "Toutes les activités loisirs à {city} avec les bons plans négociés par PROXIreduc-28 pour vous faire profiter de votre pouvoir d'achat local."
  },
  artisans: {
    h1: "Artisans et savoir-faire à {city}",
    intro: "Les artisans partenaires de PROXIreduc-28 à {city} vous proposent leurs services et créations avec des avantages réservés au réseau local."
  },
  default: {
    h1: "Bons plans {catName} à {city}",
    intro: "Toutes les offres {catName} proposées par les professionnels partenaires de PROXIreduc-28 à {city}. Un réseau 100% local d'Eure-et-Loir."
  },
};

export default function CategoryCityPage() {
  const { combinedSlug } = useParams();
  const [state, setState] = useState({ loading: true, invalid: false, category: null, city: null });
  const [offers, setOffers] = useState([]);

  // Alias "SEO-friendly" vers catégories réelles. Évite d'avoir à renommer les slugs en base.
  const CATEGORY_ALIASES = {
    "restaurants": "restauration",
    "restaurant": "restauration",
    "boulangeries": "alimentation",
    "artisans": "artisanat",
    "artisan": "artisanat",
    "commerces": "commerces",
    "mode": "mode",
    "beaute": "beaute-bien-etre",
    "coiffeurs": "beaute-bien-etre",
    "auto": "automobile",
    "voiture": "automobile",
    "sport": "sport",
    "loisirs": "loisirs",
    "culture": "culture",
    "hotels": "hotels",
    "hotel": "hotels",
    "sante": "sante",
    "bricolage": "bricolage",
    "maison": "maison",
  };

  // Essaie de parser le combinedSlug : fetch catégories + villes, et trouve la combinaison
  useEffect(() => {
    const run = async () => {
      if (!combinedSlug || !combinedSlug.includes("-")) {
        setState({ loading: false, invalid: true }); return;
      }
      try {
        const [catRes, cityRes, subRes] = await Promise.all([api.get("/categories"), api.get("/cities"), api.get("/subcategories")]);
        const cats = catRes.data || [];
        const cities = cityRes.data || [];
        const subs = subRes.data || [];
        const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
        // Build a map { normalized_slug_or_alias -> actual_category_object }
        const catByKey = new Map();
        for (const c of cats) {
          catByKey.set(norm(c.slug || c.name), c);
          catByKey.set(norm(c.name), c);
        }
        for (const [alias, realSlug] of Object.entries(CATEGORY_ALIASES)) {
          const real = cats.find((c) => norm(c.slug) === norm(realSlug));
          if (real) catByKey.set(norm(alias), real);
        }
        let foundCat = null, foundCity = null;
        // 0) Essai sous-catégorie (métier) × ville en priorité, ex: /plombier-chartres
        const subByKey = new Map();
        for (const s of subs) { subByKey.set(norm(s.slug || s.name), s); subByKey.set(norm(s.name), s); }
        for (const key of subByKey.keys()) {
          if (key && combinedSlug.startsWith(key + "-")) {
            const rest = combinedSlug.slice(key.length + 1);
            const matchCity = cities.find((v) => norm(v.name) === rest);
            if (matchCity) {
              const sub = subByKey.get(key);
              const parent = cats.find((c) => c.id === sub.category_id) || null;
              if (parent) {
                setState({ loading: false, invalid: false, category: parent, city: matchCity, subcategory: sub });
                const { data } = await api.get(`/offers?subcategory=${encodeURIComponent(sub.slug)}&city=${encodeURIComponent(matchCity.name)}&limit=60`);
                setOffers(data || []);
                return;
              }
            }
          }
        }
        for (const key of catByKey.keys()) {
          if (combinedSlug.startsWith(key + "-")) {
            const rest = combinedSlug.slice(key.length + 1);
            const matchCity = cities.find((v) => norm(v.name) === rest);
            if (matchCity) { foundCat = catByKey.get(key); foundCity = matchCity; break; }
          }
        }
        if (!foundCat || !foundCity) { setState({ loading: false, invalid: true }); return; }
        setState({ loading: false, invalid: false, category: foundCat, city: foundCity });
        const { data } = await api.get(`/offers?category=${encodeURIComponent(foundCat.slug)}&city=${encodeURIComponent(foundCity.name)}&limit=60`);
        setOffers(data || []);
      } catch {
        setState({ loading: false, invalid: true });
      }
    };
    run();
  }, [combinedSlug]);

  useEffect(() => {
    if (state.loading || state.invalid || !state.category || !state.city) return;
    const city = state.city.name;
    const cat = state.subcategory ? state.subcategory.name : state.category.name;
    const intro = state.subcategory ? CATEGORY_INTRO.default : (CATEGORY_INTRO[state.category.slug] || CATEGORY_INTRO.default);
    const h1 = state.subcategory ? `${cat} à ${city}` : intro.h1.replace("{city}", city).replace("{catName}", cat.toLowerCase());
    document.title = `${h1} | PROXIreduc-28`;
    let md = document.querySelector('meta[name="description"]');
    if (!md) { md = document.createElement("meta"); md.name = "description"; document.head.appendChild(md); }
    const desc = intro.intro.replace("{city}", city).replace("{catName}", cat.toLowerCase());
    md.setAttribute("content", desc.slice(0, 160));
    let cn = document.querySelector('link[rel="canonical"]');
    if (!cn) { cn = document.createElement("link"); cn.setAttribute("rel", "canonical"); document.head.appendChild(cn); }
    cn.setAttribute("href", `${window.location.origin}/${combinedSlug}`);
    // JSON-LD BreadcrumbList + ItemList
    const existing = document.getElementById("jsonld-catcity");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = "jsonld-catcity";
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "BreadcrumbList", "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${window.location.origin}/` },
          { "@type": "ListItem", "position": 2, "name": "Bons plans", "item": `${window.location.origin}/bons-plans` },
          { "@type": "ListItem", "position": 3, "name": `${cat} à ${city}`, "item": window.location.href },
        ]},
        { "@type": "ItemList", "name": h1, "numberOfItems": offers.length,
          "itemListElement": offers.slice(0, 20).map((o, i) => ({
            "@type": "ListItem", "position": i + 1,
            "url": `${window.location.origin}/offre/${o.id}`,
            "name": o.title,
          })) },
      ],
    });
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [state, offers]);

  if (state.loading) return <div className="p-10 text-center text-slate-500" data-testid="catcity-loading">Chargement…</div>;
  if (state.invalid) return <Navigate to="/" replace/>;

  const cat = state.category;
  const city = state.city;
  const sub = state.subcategory;
  const displayName = sub ? sub.name : cat.name;
  const intro = sub ? CATEGORY_INTRO.default : (CATEGORY_INTRO[cat.slug] || CATEGORY_INTRO.default);
  const h1 = sub ? `${displayName} à ${city.name}` : intro.h1.replace("{city}", city.name).replace("{catName}", cat.name.toLowerCase());
  const introText = intro.intro.replace("{city}", city.name).replace("{catName}", displayName.toLowerCase());

  return (
    <div data-testid="catcity-page">
      <section className="bg-gradient-to-br from-orange-50 via-white to-emerald-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <nav className="text-xs text-slate-500 flex items-center gap-1 flex-wrap mb-3" data-testid="catcity-breadcrumb">
            <Link to="/" className="hover:text-[#E05638]">Accueil</Link>
            <span>›</span>
            <Link to="/bons-plans" className="hover:text-[#E05638]">Bons plans</Link>
            <span>›</span>
            <Link to={`/bons-plans/${cat.slug}`} className="hover:text-[#E05638]">{cat.name}</Link>
            <span>›</span>
            <span className="text-slate-800 font-semibold">{city.name}</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold uppercase tracking-widest">
            <MapPin size={14}/> {city.name} · Eure-et-Loir
          </div>
          <h1 className="mt-3 font-heading text-3xl md:text-5xl font-extrabold text-slate-900" data-testid="catcity-title">
            {h1}
          </h1>
          <p className="mt-3 text-slate-700 max-w-3xl md:text-lg" data-testid="catcity-intro">{introText}</p>
          <div className="mt-4 text-sm text-slate-500" data-testid="catcity-count">
            <b>{offers.length}</b> offre{offers.length > 1 ? "s" : ""} active{offers.length > 1 ? "s" : ""}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {offers.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {offers.map((o) => (<OfferCard key={o.id} offer={o}/>))}
          </div>
        ) : (
          <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center" data-testid="catcity-empty">
            <Store className="mx-auto text-slate-300" size={40}/>
            <div className="mt-3 text-slate-600">Aucune offre {cat.name.toLowerCase()} à {city.name} pour le moment.</div>
            <p className="mt-2 text-sm text-slate-500">Le réseau se développe chaque semaine — revenez bientôt ou découvrez les offres similaires.</p>
            <div className="mt-4 flex flex-wrap gap-3 justify-center">
              <Link to={`/bons-plans/${cat.slug}`}><Button variant="outline">Toutes les offres {cat.name.toLowerCase()}</Button></Link>
              <Link to={`/bons-plans?city=${encodeURIComponent(city.name)}`}><Button className="btn-shine text-white">Tous les bons plans à {city.name}</Button></Link>
            </div>
          </div>
        )}

        <div className="mt-10 bg-slate-50 border border-slate-200 rounded-2xl p-6">
          <h2 className="font-heading text-xl font-bold">Pourquoi PROXIreduc-28 à {city.name} ?</h2>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            <li>• Un réseau local de commerçants, artisans et professionnels d'Eure-et-Loir.</li>
            <li>• Des avantages négociés exclusivement pour les habitants de {city.name} et de l'agglomération.</li>
            <li>• 100 % gratuit pour les CSE, clubs et associations de {city.name}.</li>
            <li>• Un Pass numérique sécurisé pour profiter des offres chez les partenaires.</li>
          </ul>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to={`/bons-plans/ville/${(city.slug || city.name.toLowerCase())}`}>
              <Button variant="outline" className="h-10">Explorer tous les bons plans de {city.name} <ArrowRight size={14} className="ml-1"/></Button>
            </Link>
            <Link to="/devenir-partenaire">
              <Button className="h-10 btn-shine text-white">Devenir partenaire</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
